import { Alert, Group, Loader, Stack, Text } from '@mantine/core';

import type {
  RegistryOrderConversation,
  RegistryOrderReplyRequest,
} from '@/types/registry-documents';
import { preparedNote } from './conversationTargets';
import { RegistryChatPanel } from './RegistryChatPanel';
import classes from './RegistryConversation.module.css';

/** What the conversation says before any order has gone to Armadillo. */
export const NO_ORDERS_SENT = 'No orders sent to Armadillo on this report yet';

interface RegistryConversationProps {
  reportId: string;
  /** The report's conversation; null until the first read lands. */
  conversation: RegistryOrderConversation | null;
  /** Send a reply to one of the research desk's emails, then re-read.
   * Throws on failure: the composer shows it and keeps the text. */
  onReply: (orderId: string, request: RegistryOrderReplyRequest) => Promise<unknown>;
  /** The first read is in flight. */
  loading?: boolean;
  /** The last read failed; the conversation shown, if any, is older. */
  error?: Error | null;
  /** What the composer answers first, as the `?aside=armadillo&target=`
   * deep link named it (the Reply controls on the orders card and the
   * admin page). A later value points the composer again. */
  initialTarget?: string | null;
}

/**
 * Armadillo as a network partner: its conversation on one report, where
 * every other partner has a Freshdesk chat. Composed from the report's
 * registry orders (the research desk's emails on each, and the replies sent
 * from the Hub through the shared mailbox), not from a chat thread, and
 * laid out like one: a reply names one desk email on one order, which the
 * composer's header says.
 */
export function RegistryConversation({
  reportId,
  conversation,
  onReply,
  loading = false,
  error = null,
  initialTarget = null,
}: Readonly<RegistryConversationProps>) {
  return (
    <Stack gap="sm" className={classes.root} data-testid={`registry-conversation-${reportId}`}>
      {error && (
        <Alert color="red" title="Could not load Armadillo's conversation">
          {error.message}
        </Alert>
      )}
      {!conversation && loading && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Loading Armadillo&apos;s conversation…
          </Text>
        </Group>
      )}
      {/* Before any order is sent there is nothing to answer, and the empty
          conversation already says so. */}
      {conversation && conversation.items.length === 0 && (
        <EmptyNote orders={conversation.orders} />
      )}
      {conversation && conversation.items.length > 0 && (
        <RegistryChatPanel
          reportId={reportId}
          conversation={conversation}
          onReply={onReply}
          initialTarget={initialTarget}
        />
      )}
    </Stack>
  );
}

function EmptyNote({ orders }: Readonly<{ orders: RegistryOrderConversation['orders'] }>) {
  const prepared = preparedNote(orders);
  return (
    <Text size="sm" c="dimmed" data-testid="registry-conversation-empty">
      {NO_ORDERS_SENT}
      {prepared ? ` (${prepared}).` : '.'}
    </Text>
  );
}
